import Comments from 'components/comments'
import ScrollTopAndComment from 'components/ScrollTopAndComment'
import { ArticleSEO } from 'components/SEO'
import ShareArticle from 'components/ShareArticle'
import PageTitle from 'components/text/PageTitle'
import { Author, Snippet } from 'contentlayer/generated'
import siteMetadata from 'data/siteMetadata'
import useViewCounter from 'hooks/useViewCounter'
import { formatDate } from 'lib/utils/date'
import React, { FunctionComponent } from 'react'
import Link from 'components/Link'
import Ad from 'components/Ad'
import Newsletter from 'components/Newsletter'
import TextDataLoader from 'components/loaders/TextDataLoader'

interface ISnippetLayoutProps {
  content: Snippet
  authorDetails: Author[]
}

const SnippetLayout: FunctionComponent<ISnippetLayoutProps> = ({
  content,
  authorDetails,
  children,
}): JSX.Element => {
  const { slug, date, lastmod, title, summary, coverUrl, readingTime } = content
  const url = `${siteMetadata.siteUrl}${slug}`
  const { data, isLoading } = useViewCounter({
    slug,
    type: 'snippet',
  })

  return (
    <>
      <ArticleSEO
        url={`${siteMetadata.siteUrl}${slug}`}
        title={title}
        summary={summary}
        authorDetails={authorDetails}
        coverUrl={coverUrl}
        date={date}
        lastmod={lastmod}
        readingTime={readingTime.text}
        key={title}
      />
      <ScrollTopAndComment />
      <div>
        <header>
          <div className="pb-10 space-y-1 text-center">
            <dl>
              <div>
                <dt className="sr-only">Published on</dt>
                <dd className="text-base font-medium leading-6 text-gray-500 dark:text-gray-400">
                  <time dateTime={date}>{formatDate(date)}</time>
                </dd>
              </div>
            </dl>
            <div>
              <PageTitle>{title}</PageTitle>
            </div>
          </div>
        </header>
        <hr className="hr" />
        <div className="mx-auto flex flex-col-reverse justify-between lg:flex-row lg:gap-6 lg:px-0 mb-8">
          <div className="lg:max-w-3xl lg:mx-auto px-0 lg:px-6 xl:px-0">
            <div>
              <article className="pt-10 pb-8 prose dark:prose-dark max-w-none">{children}</article>
              <div className="mt-6">
                <Newsletter />
              </div>
              <div className="lg:hidden">
                <ShareArticle url={url} title={title} coverUrl={coverUrl} />
              </div>
            </div>
          </div>
          <aside className="pt-10 top-16 lg:sticky lg:h-full w-full lg:w-80">
            <div className="space-y-6">
              <div className="hidden lg:block">
                <ShareArticle url={url} title={title} coverUrl={coverUrl} />
              </div>
              <div className="h-fit">
                <Ad />
              </div>
            </div>
          </aside>
        </div>
        <Comments frontMatter={content} />
        <footer>
          <div className="pt-4 xl:pt-8 flex justify-between items-center">
            <Link
              href="/snippets"
              className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
            >
              &larr; Back to the snippets
            </Link>
            <div>
              <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Views
              </h2>
              {isLoading ? (
                <TextDataLoader />
              ) : (
                <span className="text-primary-500">
                  {Number(data.total || 0).toLocaleString()} views
                </span>
              )}
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

export default SnippetLayout
